/**
 * Lozenge — the status pill. 11px uppercase at `font-[700]` (tiny labels need the
 * full Inter bump). `bold` inverts to a solid fill for states that must shout.
 */
import type { JSX, ParentProps } from "solid-js";
import { cn } from "./utils/cn";

export type LozengeTone =
  | "default"
  | "success"
  | "danger"
  | "warning"
  | "information"
  | "discovery";

const SUBTLE: Record<LozengeTone, string> = {
  default: "bg-[var(--c9-bg-neutral)] text-[color:var(--c9-text-subtle)]",
  success: "bg-[var(--c9-bg-success)] text-[color:var(--c9-text-success)]",
  danger: "bg-[var(--c9-bg-danger)] text-[color:var(--c9-text-danger)]",
  warning: "bg-[var(--c9-bg-warning)] text-[color:var(--c9-text-warning)]",
  information: "bg-[var(--c9-bg-information)] text-[color:var(--c9-text-information)]",
  discovery: "bg-[var(--c9-bg-discovery)] text-[color:var(--c9-text-discovery)]",
};

const BOLD: Record<LozengeTone, string> = {
  default: "bg-[var(--c9-bg-neutral-bold)] text-[color:var(--c9-text-inverse)]",
  success: "bg-[var(--c9-bg-success-bold)] text-[color:var(--c9-text-inverse)]",
  danger: "bg-[var(--c9-bg-danger-bold)] text-[color:var(--c9-text-inverse)]",
  warning: "bg-[var(--c9-bg-warning-bold)] text-[color:var(--c9-text)]",
  information: "bg-[var(--c9-bg-information-bold)] text-[color:var(--c9-text-inverse)]",
  discovery: "bg-[var(--c9-bg-discovery-bold)] text-[color:var(--c9-text-inverse)]",
};

export function Lozenge(
  props: ParentProps<{ tone?: LozengeTone; bold?: boolean; class?: string }>,
): JSX.Element {
  const tone = () => props.tone ?? "default";
  return (
    <span
      class={cn(
        "inline-flex max-w-full items-center truncate rounded-xsmall px-050 py-025",
        "text-[11px] font-[700] uppercase leading-[16px] tracking-[0.02em]",
        props.bold ? BOLD[tone()] : SUBTLE[tone()],
        props.class,
      )}
    >
      {props.children}
    </span>
  );
}

/** Neutral quantity chip — "this tab holds 12 rows". NOT an alert. */
export function CountChip(props: { value: number | string; class?: string }): JSX.Element {
  return (
    <span
      class={cn(
        "inline-flex h-4 min-w-4 items-center justify-center rounded-xsmall px-050",
        "bg-[var(--c9-bg-neutral)] text-body-s font-[630] tabular-nums text-[color:var(--c9-text-subtle)]",
        props.class,
      )}
    >
      {props.value}
    </span>
  );
}
